import React, {useState, useEffect, useRef, useMemo, useCallback} from "react";
import { saveAs } from "file-saver";
import Papa from "papaparse";
import MapComponent from "./MapComponent";
import {ApiPlace, EvConnector, Marker, PlacesBySource, StationStatus, ChargingLevel, PlugType} from "../types/types"
import api from "../Api";
import {getMapApiKey, getMapId} from "../TokenService";
import {Navbar} from "./Navbar";
import {calculateZoomLevel} from "./utils/MapUtils";
// type PlacesBySource = Record<string, Record<string, ApiPlace>>

const SOURCE_COLORS: Record<string, string> = {
    Google: '#4285F4',
    Elastic: '#EA4335',
    NREL: '#34A853',
    // Redshift: '#F4B400'
};
const STATUS_OPTIONS = ['Available', 'Busy', "Out of Service"] as StationStatus[];
const LEVEL_OPTIONS = ['Level2 Charger', 'DC Fast/Level3 Charger'] as ChargingLevel[];
const PLUG_OPTIONS = ['J1772', 'CCS1', 'CHAdeMO', 'TESLA'] as PlugType[];
const SOURCE_OPTIONS = Object.keys(SOURCE_COLORS);
const CLUSTER_THRESHOLD = Number.MAX_SAFE_INTEGER;

const SearchPage: React.FC = () => {
    const [placesBySource, setPlacesBySource] = useState<PlacesBySource>({});
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('Toyota HQ Plano, TX');
    const [center, setCenter] = useState({lat: 33.0554, lng: -96.8311});
    const [radius, setRadius] = useState(5);
    const [lastSearchType, setLastSearchType] = useState<'text' | 'nearby'>('text');
    const [sources, setSources] = useState<string[]>(['Google']);
    const [operators, setOperators] = useState<string[]>([]);
    const [statuses, setStatuses] = useState<StationStatus[]>(STATUS_OPTIONS);
    const [levels, setLevels] = useState<ChargingLevel[]>(LEVEL_OPTIONS);
    const [plugTypes, setPlugTypes] = useState<PlugType[]>(PLUG_OPTIONS);

    const [showExportMenu, setShowExportMenu] = useState(false);

    const searchTypeRef = useRef<'text' | 'nearby'>(lastSearchType);
    const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const [shouldRecenter, setShouldRecenter] = useState(false);
    const handleRecentered = () => {
        console.log('map recentered');
        setShouldRecenter(false);
    };
    const computeStationStatus = (conns: EvConnector[] | null) => {
        const list = Array.isArray(conns) ? conns : [];

        const {total, available, outOfService} = list.reduce(
            (acc, c) => {
                acc.total += c.count;
                acc.available += c.availableCount;
                acc.outOfService += c.outOfServiceCount;
                return acc;
            },
            {total: 0, available: 0, outOfService: 0}
        );

        const busy = total - available - outOfService;

        if (available > 0) return 'Available';
        if (busy > 0) return 'Busy';
        return 'Out of Service';
    };

    const mapConnectorToLevel = (type: string) =>
        (type === 'J1772' || type === 'CCS1') ? 'Level2 Charger' : 'DC Fast/Level3 Charger';

    const allMarkers = useMemo<Marker[]>(() =>
        Object.entries(placesBySource).flatMap(([src, group]) =>
            Object.values(group).map(place => {
                const conns = Array.isArray(place.chargingConnectors) ? place.chargingConnectors : [];
                const status = computeStationStatus(conns);
                const plugSet = Array.from(new Set(conns.map(c => c.type).filter(t => PLUG_OPTIONS.includes(t as any)))) as PlugType[];
                const levelSet = Array.from(new Set(conns.map(c => mapConnectorToLevel(c.type)))) as ChargingLevel[];
                return {
                    source: src, lat: place.lat, lng: place.lng, name: place.name, address: place.address,
                    evConnectors: conns, status, plugTypes: plugSet, levels: levelSet,
                    operator: (place as ApiPlace & { operator?: string }).operator || 'Unknown'
                };
            })
        ), [placesBySource]
    );


    const operatorOptions = useMemo(() => {
        return Array.from(new Set(allMarkers.map(m => m.operator))).filter(o => o);
    }, [allMarkers]);

    const filteredMarkers = useMemo(
        () => {
            return allMarkers.filter(m =>
                (sources.length > 0 && sources.includes(m.source)) &&
                (operators.length > 0 && operators.includes(m.operator)) &&
                (statuses.length > 0 && statuses.includes(m.status)) &&
                (levels.length > 0 && m.levels.some(l => levels.includes(l))) &&
                (plugTypes.length > 0 && m.plugTypes.some(p => plugTypes.includes(p)))
            )
        }, [allMarkers, sources, operators, statuses, levels, plugTypes]
    );

    const sourceCounts = useMemo(() => {
        return SOURCE_OPTIONS.reduce((acc, src) => {
            acc[src] = filteredMarkers.filter(m => m.source === src).length;
            return acc;
        }, {} as Record<string, number>);
    }, [filteredMarkers]);

    const fetchTextSearch = useCallback(async (loading: boolean) => {
        // if (!query.trim() || !sources.includes('Google')) return;
        if (!query.trim() || sources.length === 0) {
            console.warn(`[fetchTextSearch] no query or no sources`);
            return;
        }
        setLastSearchType('text');
        searchTypeRef.current = 'text';
        setLoading(loading);

        console.debug(`[SearchPage] fetchTextSearch query: ${query} with radius: ${radius}`);
        try {
            const response = await api.post('/search/text', {
                source: sources,
                textQuery: query,
                radius: radius
            });
            console.log(`[fetchTextSearch] sources: ${sources}`)
            // 1) Grab the raw payload
            const raw = response.data as Record<string, any>;
            // 2) Separate center vs. actual sources
            const newPlaces: Record<string, any> = {};
            Object.entries(raw).forEach(([key, val]) => {
                if (SOURCE_OPTIONS.includes(key)) {
                    console.log(`[fetchTextSearch] key: ${key}`);
                    // this key is an actual data‐source map
                    newPlaces[key] = val;
                } else if (val && typeof val.lat === 'number' && typeof val.lng === 'number') {
                    // treat anything else with lat/lng as the center
                    setCenter({lat: val.lat, lng: val.lng});
                }
            });
            console.log('Redshift places:', newPlaces.Redshift);
            console.log('Google places:', newPlaces.Google);
            setPlacesBySource(newPlaces as PlacesBySource);
            // setCenter({ lat: response.data, lng: -95.370541 });

            // reset filters
            const newMarkers = Object.entries(newPlaces).flatMap(([src, group]) =>
                Object.values(group).map(place => ({
                    operator: (place as any).operator || 'Unknown'
                }))
            );
            const allOps = Array.from(new Set(newMarkers.map(m => m.operator))).filter(o => o);
            if (loading) {
                setOperators(allOps);
                setStatuses(STATUS_OPTIONS);
                setLevels(LEVEL_OPTIONS);
                setPlugTypes(PLUG_OPTIONS);
            }
        } catch (error) {
            console.error(`[SearchPage] failed to fetch places: ${error}`);
        } finally {
            setLoading(false);
        }
    }, [query, radius, sources]);

    const fetchNearbySearch = useCallback(async (loading: boolean) => {
        // if (!sources.includes('Google')) return;
        if (sources.length === 0) {
            console.warn(`[fetchNearbySearch] no sources`);
            return;
        }
        console.warn(`[fetchNearbySearch] sources length: ${sources.length}`);
        setLastSearchType('nearby');
        searchTypeRef.current = 'nearby';
        setLoading(loading);

        try {
            setCenter({lat: 30.39234, lng: -97.74051});
            setRadius(Math.round(1.609));
            console.debug(`[SearchPage] fetchNearbySearch center: ${center} with radius: ${radius}`);

            const response = await api.post('/search/nearby', {
                source: sources,
                radius: radius,
                center: center
            });
            console.log(`[fetchNearbySearch] sources: ${sources}`);
            const raw = response.data as Record<string, any>;
            const newPlaces: Record<string, any> = {};
            Object.entries(raw).forEach(([key, val]) => {
                if (SOURCE_OPTIONS.includes(key)) {
                    newPlaces[key] = val;
                } else if (val && typeof val.lat === 'number' && typeof val.lng === 'number') {
                    setCenter({lat: val.lat, lng: val.lng});
                }
            });

            setPlacesBySource(newPlaces as PlacesBySource);
            // reset filters
            const newMarkers = Object.entries(newPlaces).flatMap(([src, group]) =>
                Object.values(group).map(place => ({
                    operator: (place as any).operator || 'Unknown'
                }))
            );
            const allOps = Array.from(new Set(newMarkers.map(m => m.operator))).filter(o => o);
            if (loading) {
                setOperators(allOps);
                setStatuses(STATUS_OPTIONS);
                setLevels(LEVEL_OPTIONS);
                setPlugTypes(PLUG_OPTIONS);
            }
        } catch (error) {
            console.error(`[SearchPage] failed to fetch nearby places: ${error}`);
        } finally {
            setLoading(false);
        }
    }, [sources, radius, center]);

    const handleExportFormat = (format: "json" | "csv") => {
        if (filteredMarkers.length === 0) {
            alert('No data available to export!');
            return;
        }

        //prompt the window
        const timestamp = new Date().toISOString().replaceAll(/[:.]/g, "-");
        if (format === "json") {
            const blob = new Blob([JSON.stringify(filteredMarkers, null, 2)], {
                type: "application/json;charset=utf-8",
            });
            saveAs(blob, `map_data_${timestamp}.${format}`);
        } else if (format === "csv") {
            const csv = Papa.unparse(
                filteredMarkers.map(m => ({
                    name: m.name,
                    address: m.address,
                    source: m.source,
                    operator: m.operator,
                    status: m.status,
                    plugTypes: m.plugTypes.join(", "),
                    levels: m.levels.join(", "),
                    latitude: m.lat,
                    longitude: m.lng,
                }))
            );
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
            saveAs(blob, `map_data_${timestamp}.${format}`);
        }
    }
    const handleSearchClick = async () => {
        if (refreshTimerRef.current) {
            clearInterval(refreshTimerRef.current);
        }
        if (searchTypeRef.current === 'text') {
            await fetchTextSearch(true);
        } else {
            await fetchNearbySearch(true);
        }
        setShouldRecenter(true);

        refreshTimerRef.current = setInterval(() => {
            if (searchTypeRef.current === 'text') {
                console.log('[SearchPage] Refreshing text search');
                fetchTextSearch(false);
            } else {
                console.log('[SearchPage] Refreshing nearby search');
                fetchNearbySearch(false);
            }
        }, 5 * 60 * 1000);
    };
    // clean up timer
    useEffect(() => {
        return () => {
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
            }
        }
    }, []);

    const toggle = <T extends string>(
        item: T,
        list: T[],
        setList: React.Dispatch<React.SetStateAction<T[]>>
    ) => {
        console.log(`[toggle] update items: ${item} and current list: ${list}`);
        setList(prev =>
            prev.includes(item)
                ? prev.filter(x => x !== item)
                : [...prev, item]
        );
    };

    const mapApiKey = getMapApiKey()!;
    const mapId = getMapId()!;

    return (
        <>
            <Navbar />
            <div className="container-fluid">
                <div className="d-flex vh-100">
                    <aside className="bg-light border-end p-4"
                           style={{
                               width: '25%',
                               position: 'sticky',
                               top: '70px',
                               height: 'calc(100vh - 70px)',
                               overflowY: 'auto'
                           }}
                    >
                    {/* Summary Card */}
                        <div className="card mb-3 shadow-sm" style={{backgroundColor: '#EB0A1E', color: 'white'}}>
                            <div className="card-body text-start">
                                <h4 className="card-title fw-bold fst-italic text-white">Summary</h4>
                                <p><strong>Location:</strong> {query}</p>
                                <p><strong>Current Center:</strong> {center.lat.toFixed(4)}, {center.lng.toFixed(4)}</p>
                                <p><strong>Total Stations:</strong> {filteredMarkers.length}</p>
                                {SOURCE_OPTIONS.map(src => (
                                    <p key={src}>
                                        <strong>{src} Stations:</strong> {sourceCounts[src]}
                                    </p>
                                ))}
                            </div>
                        </div>

                        {/* Search Controls Card */}
                        <div className="card mb-3 shadow-sm">
                            <div className="card-body text-start">
                                <h4 className="card-title">Search Controls</h4>
                                <div className="mb-3">
                                    <label className="form-label">Search Text</label>
                                    <input className="form-control" value={query}
                                           onChange={e => setQuery(e.target.value)}/>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Radius (km), max: 50km</label>
                                    <input type="number" className="form-control" value={radius || 20} min={0} max={5000}
                                           onChange={e => setRadius(parseInt(e.target.value, 10))}/>
                                </div>
                                <div className="mb-3 d-flex align-items-center gap-2">
                                    <button className="btn btn-primary"
                                            onClick={() => {
                                                searchTypeRef.current = 'text';
                                                handleSearchClick();
                                            }}
                                            disabled={loading}>
                                        🔍 Search
                                    </button>

                                    {/*<div className="position-relative">*/}
                                    {/*    <button className="btn btn-success"*/}
                                    {/*            onClick={() => setShowExportMenu(!showExportMenu)}>*/}
                                    {/*        ⬇️ Export*/}
                                    {/*    </button>*/}

                                    {/*    {showExportMenu && (*/}
                                    {/*        <div className="position-absolute mt-2 border rounded p-2 bg-light" style={{right: 0, zIndex: 100}}>*/}
                                    {/*            <button className="btn btn-sm btn-outline-primary w-100 mb-1"*/}
                                    {/*                    onClick={() => handleExportFormat("json")}>*/}
                                    {/*                Export as JSON*/}
                                    {/*            </button>*/}
                                    {/*            <button className="btn btn-sm btn-outline-secondary w-100"*/}
                                    {/*                    onClick={() => handleExportFormat("csv")}>*/}
                                    {/*                Export as CSV*/}
                                    {/*            </button>*/}
                                    {/*        </div>*/}
                                    {/*    )}*/}
                                    {/*</div>*/}
                                </div>
                                {/*<button className="btn btn-outline-primary"*/}
                                {/*        onClick={() => {*/}
                                {/*            searchTypeRef.current = 'nearby';*/}
                                {/*            handleSearchClick();*/}
                                {/*        }}*/}
                                {/*        disabled={loading}>🔍 Nearby(Debug)*/}
                                {/*</button>*/}
                                {/*<button className="btn btn-primary me-2" onClick={fetchTextSearch} disabled={loading}>🔍 Search</button>*/}
                                {/*<button className="btn btn-outline-primary" onClick={fetchNearbySearch} disabled={loading}>📍 Nearby</button>*/}
                            </div>
                        </div>

                        {/* Filters Card */}
                        <div className="card mb-3 shadow-sm">
                            <div className="card-body text-start">
                                <h4 className="card-title">Filters</h4>
                                <FilterSection<string> title="Data Sources" options={SOURCE_OPTIONS} selected={sources}
                                                       onToggle={val => toggle(val, sources, setSources)}
                                                       colors={SOURCE_COLORS}/>
                                <FilterSection<string> title="EV Operator" options={operatorOptions}
                                                       selected={operators}
                                                       onToggle={val => toggle(val, operators, setOperators)}
                                                       smallTitle/>
                                <FilterSection<StationStatus> title="Station Status" options={STATUS_OPTIONS}
                                                              selected={statuses}
                                                              onToggle={val => toggle(val, statuses, setStatuses)}
                                                              smallTitle/>
                                <FilterSection<ChargingLevel> title="Charging Level" options={LEVEL_OPTIONS}
                                                              selected={levels}
                                                              onToggle={val => toggle(val, levels, setLevels)}
                                                              smallTitle/>
                                <FilterSection<PlugType> title="Plug Type" options={PLUG_OPTIONS} selected={plugTypes}
                                                         onToggle={val => toggle(val, plugTypes, setPlugTypes)}
                                                         smallTitle/>
                            </div>
                        </div>
                    </aside>

                    {/* Map */}
                    <div className="col-md-9 p-0">
                        {loading ?
                            <div className="d-flex justify-content-center align-items-center h-100">
                                <div className="spinner-border text-primary" role="status"><span
                                    className="visually-hidden">Loading...</span></div>
                            </div>
                            :
                            <MapComponent
                                apiKey={mapApiKey}
                                mapId={mapId}
                                center={center}
                                // zoom={calculateZoomLevel(radius)}
                                markers={filteredMarkers}
                                sourceColors={SOURCE_COLORS}
                                onMapClick={setCenter}
                                onRadiusChange={r => setRadius(Math.round(r))}
                                CLUSTER_THRESHOLD={CLUSTER_THRESHOLD}
                                shouldRecenter={shouldRecenter}
                                onRecentered={handleRecentered}
                            />
                        }
                    </div>
                </div>
            </div>
        </>
    );
};

export default SearchPage;

export type FilterSectionProps<T extends string> = {
    title: string;
    options: readonly T[];
    selected: T[];
    onToggle: (val: T) => void;
    colors?: Record<string, string>;
    smallTitle?: boolean;
};

export function FilterSection<T extends string>({ title, options, selected, onToggle, colors, smallTitle = false }: Readonly<FilterSectionProps<T>>) {
    return (
        <div className="mb-3">
            <h5 className={`mb-2 ${smallTitle ? 'fs-6' : 'fs-5'}`}>{title}</h5>
            <div className="ps-2">
                {options.map(opt => (
                    <div className="form-check" key={opt}>
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={`${title}-${opt}`}
                            checked={selected.includes(opt)}
                            onChange={() => onToggle(opt)}
                        />
                        {colors && colors[opt] && (
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: 12,
                                    height: 12,
                                    backgroundColor: colors[opt],
                                    borderRadius: 2,
                                    margin: '0 6px'
                                }}
                            />
                        )}
                        <label className="form-check-label small fw-bold" htmlFor={`${title}-${opt}`}>{opt}</label>
                    </div>
                ))}
            </div>
        </div>
    );
}